import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  AUTH_FORCE_LOGOUT_CHANNEL,
  AUTH_LOGOUT_EVENT,
  clearSession,
  shouldForceLogoutCurrentUser
} from '../utils/auth';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;

// Only log in development mode
const isDevelopment = process.env.NODE_ENV === 'development';
const debugLog = (...args) => {
  if (isDevelopment) {
    console.log(...args);
  }
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  // Helper to add a notification only if it's not already present
  const addUniqueNotification = (notification, keys = []) => {
    // Default uniqueness keys if none provided
    const uniqKeys = keys.length ? keys : ['type', 'leaveId', 'employeeId', 'startDate', 'endDate'];
    let added = false;
    setNotifications(prev => {
      const exists = prev.some(n =>
        uniqKeys.every(k =>
          n[k] !== undefined &&
          notification[k] !== undefined &&
          n[k] === notification[k]
        )
      );
      if (exists) {
        return prev;
      }
      added = true;
      return [notification, ...prev];
    });
    if (added && !notification.read) {
      setUnreadCount(prev => prev + 1);
    }
  };

  useEffect(() => {
    const fetchProfile = async () => {
      const id = localStorage.getItem('id');
      
      if (!id) {
        setLoading(false);
        return;
      }

      if (!API_URL) {
        console.error('API URL is not configured');
        setLoading(false);
        return;
      }

      try {
        const payload = {
          action: 'get-profile',
          id: id
        };
        const response = await axios.post(API_URL, payload, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        const result = response.data;

        if (result?.success && result?.profile) {
          const profileData = result.profile;
          setProfile(profileData);
          
          // Update isReportingManager in localStorage if it exists in profile
          if (profileData.hasOwnProperty('isReportingManager')) {
            const currentIsReportingManager = localStorage.getItem('isReportingManager');
            const newIsReportingManager = profileData.isReportingManager ? 'true' : 'false';
            
            if (currentIsReportingManager !== newIsReportingManager) {
              localStorage.setItem('isReportingManager', newIsReportingManager);
            }
          }

          // Store designationName in localStorage if it exists
          if (profileData.designationName) {
            localStorage.setItem('designationName', profileData.designationName);
          }

          // Store isAdmin and isManagingDirector flags in localStorage
          if (profileData.hasOwnProperty('isAdmin')) {
            localStorage.setItem('isAdmin', profileData.isAdmin ? 'true' : 'false');
          }
          if (profileData.hasOwnProperty('isManagingDirector')) {
            localStorage.setItem('isManagingDirector', profileData.isManagingDirector ? 'true' : 'false');
          }
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  // WebSocket connection for notifications (admin and reporting managers)
  useEffect(() => {
    const role = localStorage.getItem('role');
    const userId = localStorage.getItem('id');
    const cachedIsReportingManager = localStorage.getItem('isReportingManager') === 'true';
    const cachedIsAdmin = localStorage.getItem('isAdmin') === 'true';
    const cachedIsManagingDirector = localStorage.getItem('isManagingDirector') === 'true';
    const designationName = localStorage.getItem('designationName');
    const adminDesignation = process.env.REACT_APP_ADMIN_DESIGNATION;
    
    // Check if user is admin by multiple methods:
    // 1. Profile flags (isAdmin or isManagingDirector) - PRIMARY CHECK
    // 2. Role from localStorage
    // 3. Designation match (fallback)
    const isAdminByProfile = profile?.isAdmin === true || profile?.isManagingDirector === true;
    const isAdminByCache = cachedIsAdmin || cachedIsManagingDirector;
    const isAdminByRole = role === 'admin';
    const designationMatch = designationName && adminDesignation && 
      designationName.trim() === adminDesignation.trim();
    const isAdminByDesignation = designationMatch;
    
    // User is admin if any of these conditions are true
    const isAdmin = isAdminByProfile || isAdminByCache || isAdminByRole || isAdminByDesignation;
    const isReportingManager = profile?.isReportingManager === true || cachedIsReportingManager;
    
    // Only connect WebSocket for admin users or reporting managers
    if (!isAdmin && !isReportingManager) {
      return;
    }

    if (!SOCKET_URL) {
      if (isDevelopment) {
        console.error('WebSocket URL is not configured');
      }
      return;
    }

    // If user is admin (by any method), they can connect immediately
    // If user is reporting manager, they need profile confirmation or cached flag
    if (!isAdmin && isReportingManager && !profile && !cachedIsReportingManager) {
      return;
    }

    const connectWebSocket = () => {
      try {
        // Create WebSocket connection with userId in query params
        // For Managing Directors, send as employee but backend will upgrade to admin
        const wsUrl = `${SOCKET_URL}?userId=${encodeURIComponent(userId)}&userType=${encodeURIComponent(isAdmin ? 'admin' : 'employee')}&role=${encodeURIComponent(isAdmin ? 'admin' : 'reporting-manager')}`;
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          debugLog('WebSocket connected');
          reconnectAttempts.current = 0;
          
          // Join admin room if admin, or just register connection for reporting manager
          if (isAdmin) {
            ws.send(JSON.stringify({
              action: 'join-admin-room',
              role: 'admin',
              userType: 'admin',
              userId: userId
            }));
          } else {
            // For reporting managers, just send a ping to register the connection
            ws.send(JSON.stringify({
              action: 'ping',
              userId,
              userType: 'employee',
              role: 'reporting-manager',
              timestamp: new Date().toISOString()
            }));
          }

          // Start ping interval to keep connection alive (every 30 seconds)
          pingIntervalRef.current = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({
                action: 'ping',
                userId,
                userType: isAdmin ? 'admin' : 'employee',
                role: isAdmin ? 'admin' : 'reporting-manager',
                timestamp: new Date().toISOString()
              }));
              debugLog('Ping sent to keep connection alive');
            }
          }, 30000); // 30 seconds
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            debugLog('WebSocket message received:', data);

            // Handle admin room join confirmation
            if (data.type === 'admin-room-joined') {
              debugLog(' Successfully joined admin room');
              return;
            }

            // Handle admin registration confirmation (for Managing Directors)
            if (data.type === 'admin-registered') {
              debugLog('Successfully registered as admin (Managing Director)');
              return;
            }

            // Handle pong response (keepalive)
            if (data.type === 'pong') {
              debugLog(' Pong received - connection alive');
              return;
            }

            // Handle employee waiting approval notifications (admin only)
            if (data.type === 'employee-waiting-approval') {
              debugLog('New employee waiting for approval:', data);
              
              const notification = {
                id: Date.now(),
                type: data.type || 'employee_registration',
                title: data.title || 'Employee Waiting for Approval',
                message: data.message || `${data.employeeName || 'An employee'} has completed registration and is waiting for approval`,
                employeeId: data.employeeId,
                employeeName: data.employeeName,
                employeeEmail: data.employeeEmail,
                status: data.status || 'waiting for approval',
                timestamp: data.timestamp || new Date().toISOString(),
                data: data.data || {},
                read: false
              };
              addUniqueNotification(notification, ['type', 'employeeId', 'timestamp']);
            }

            // Handle leave application notifications (admin and reporting managers)
            if (data.type === 'leave-applied') {
              debugLog('New leave application:', data);
              
              const notification = {
                id: data.leaveId || Date.now(),
                type: data.type || 'leave_application',
                title: data.title || 'Leave Application Submitted',
                message: data.message || `${data.employeeName || 'An employee'} has applied for ${data.leaveType || 'leave'}`,
                leaveId: data.leaveId,
                employeeId: data.employeeId,
                employeeName: data.employeeName,
                employeeEmail: data.employeeEmail,
                leaveType: data.leaveType,
                startDate: data.startDate,
                endDate: data.endDate,
                numberOfDays: data.numberOfDays,
                reason: data.reason || '',
                status: data.status || 'pending',
                timestamp: data.timestamp || new Date().toISOString(),
                data: data.data || {},
                read: false
              };
              addUniqueNotification(notification, ['type', 'leaveId', 'employeeId', 'startDate', 'endDate']);
            }
          } catch (error) {
            if (isDevelopment) {
              console.error('Error parsing WebSocket message:', error);
            }
          }
        };

        ws.onerror = (error) => {
          if (isDevelopment) {
            console.error(' WebSocket error:', error);
          }
        };

        ws.onclose = (event) => {
          debugLog('🔌 WebSocket disconnected:', event.code, event.reason);
          
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
          }
          
          if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
            reconnectAttempts.current += 1;
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
            debugLog(`Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current}/${maxReconnectAttempts})...`);
            reconnectTimeoutRef.current = setTimeout(() => {
              connectWebSocket();
            }, delay);
          } else if (reconnectAttempts.current >= maxReconnectAttempts) {
            if (isDevelopment) {
              console.error('Max reconnection attempts reached');
            }
          }
        };
      } catch (error) {
        if (isDevelopment) {
          console.error('Error creating WebSocket connection:', error);
        }
      }
    };

    // Initial connection
    // If user is admin (by any method), connect immediately
    // If user is reporting manager, wait for profile to confirm
    if (isAdmin) {
      debugLog('Connecting WebSocket as admin (by profile, cache, role, or designation)');
      connectWebSocket();
    } else if (isReportingManager && (profile || cachedIsReportingManager)) {
      debugLog('Connecting WebSocket as reporting manager');
      connectWebSocket();
    }

    // Cleanup on unmount
    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
        wsRef.current = null;
      }
    };
  }, [profile]); // Re-run when profile changes

  // Close notifications dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNotifications && !event.target.closest('.nav-item.dropdown')) {
        setShowNotifications(false);
      }
    };
    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications]);

  // Get name from profile (handles both Employee and Admin structures)
  const getName = () => {
    if (!profile) return 'User';
    
    // Admin structure has 'name' field
    if (profile.name) {
      return profile.name;
    }
    
    // Employee structure has 'firstName' and 'lastName'
    if (profile.firstName || profile.lastName) {
      const firstName = profile.firstName || '';
      const lastName = profile.lastName || '';
      return `${firstName} ${lastName}`.trim() || 'User';
    }
    
    return 'User';
  };

  // Get first letter of name in uppercase
  const getInitial = () => {
    const name = getName();
    if (name && name !== 'User') {
      return name.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const displayName = getName();
  const initial = getInitial();

  const closeRealtimeConnections = () => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User logging out');
      wsRef.current = null;
    }
  };

  const handleLogout = (e) => {
    e.preventDefault();
    closeRealtimeConnections();
    const redirectPath = clearSession();
    navigate(redirectPath, { replace: true });
  };

  useEffect(() => {
    const onAppLogout = () => {
      closeRealtimeConnections();
    };

    const onForceLogoutMessage = (event) => {
      if (
        event.data?.type === 'FORCE_LOGOUT' &&
        shouldForceLogoutCurrentUser(event.data.employeeId)
      ) {
        closeRealtimeConnections();
        const redirectPath = clearSession();
        navigate(redirectPath, { replace: true });
      }
    };

    window.addEventListener(AUTH_LOGOUT_EVENT, onAppLogout);

    let channel;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        channel = new BroadcastChannel(AUTH_FORCE_LOGOUT_CHANNEL);
        channel.onmessage = onForceLogoutMessage;
      }
    } catch (error) {
      console.warn('BroadcastChannel unavailable for force logout:', error);
    }

    return () => {
      window.removeEventListener(AUTH_LOGOUT_EVENT, onAppLogout);
      if (channel) {
        channel.close();
      }
    };
  }, [navigate]);

  // Handle notification click
  const handleNotificationClick = (notification) => {
    // Mark as read (but keep the notification visible)
    setNotifications(prev =>
      prev.map(notif =>
        notif.id === notification.id ? { ...notif, read: true } : notif
      )
    );
    
    // Decrease unread count if it was unread
    if (!notification.read) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }

    // Navigate based on notification type
    if (notification.type === 'employee-waiting-approval' && notification.employeeId) {
      // Navigate to employee view/edit page
      navigate(`/employeeedit/${notification.employeeId}?tab=employment`);
      setShowNotifications(false);
    } else if (notification.type === 'leave-applied' || notification.type === 'leave_application') {
      // Navigate to leave management page
      navigate(`/leave-management${notification.leaveId ? `?leaveId=${notification.leaveId}` : ''}`);
      setShowNotifications(false);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = () => {
    setNotifications(prev =>
      prev.map(notif => ({ ...notif, read: true }))
    );
    setUnreadCount(0);
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  };

  // Check admin status for showing notification bell
  const role = localStorage.getItem('role');
  const cachedIsReportingManager = localStorage.getItem('isReportingManager') === 'true';
  const cachedIsAdmin = localStorage.getItem('isAdmin') === 'true';
  const cachedIsManagingDirector = localStorage.getItem('isManagingDirector') === 'true';
  const designationName = localStorage.getItem('designationName');
  const adminDesignation = process.env.REACT_APP_ADMIN_DESIGNATION;
  
  const isAdminByProfile = profile?.isAdmin === true || profile?.isManagingDirector === true;
  const isAdminByCache = cachedIsAdmin || cachedIsManagingDirector;
  const isAdminByRole = role === 'admin';
  const designationMatch = designationName && adminDesignation && 
    designationName.trim() === adminDesignation.trim();
  const isAdminByDesignation = designationMatch;
  const isAdmin = isAdminByProfile || isAdminByCache || isAdminByRole || isAdminByDesignation;
  const isReportingManager = profile?.isReportingManager === true || cachedIsReportingManager;
  const employmentStatus = localStorage.getItem('employmentStatus');
  
  // Show notifications for admin or reporting managers
  const showNotificationBell = isAdmin || isReportingManager;
  
  // Hide pushmenu on waiting page when employmentStatus is "WAITING FOR APPROVAL"
  const isWaitingPage = pathname === '/waiting';
  const isWaitingForApproval = employmentStatus && 
    (employmentStatus === 'WAITING FOR APPROVAL' || 
     employmentStatus.toLowerCase() === 'waiting for approval' || 
     employmentStatus.toLowerCase() === 'waiting_for_approval');
  const shouldHidePushmenu = isWaitingPage && isWaitingForApproval;

  return (
    <div>
      <nav className="main-header navbar navbar-expand navbar-white navbar-light">
        <ul className="navbar-nav">
          {!shouldHidePushmenu && (
            <li className="nav-item">
              <div className="nav-link" data-widget="pushmenu" href="#" role="button">
                <i className="fas fa-bars" />
              </div>
            </li>
          )}
        </ul>
        <ul className="navbar-nav ml-auto">
          {/* Notifications Dropdown - Admin and Reporting Managers */}
          {showNotificationBell && (
            <li className="nav-item dropdown">
              <div
                className="nav-link"
                data-toggle="dropdown"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setShowNotifications(!showNotifications);
                }}
              >
                <i className="far fa-bell" />
                {unreadCount > 0 && (
                  <span className="badge badge-warning navbar-badge1">{unreadCount}</span>
                )}
              </div>
              <div className={`dropdown-menu dropdown-menu-lg dropdown-menu-right ${showNotifications ? 'show' : ''}`} style={{ maxHeight: '500px', overflowY: 'auto' }}>
                <span className="dropdown-item dropdown-header" style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>
                  {notifications.length} Notification{notifications.length !== 1 ? 's' : ''} ({unreadCount} unread)
                  {unreadCount > 0 && (
                    <button
                      className="btn btn-sm btn-link text-primary float-right p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        markAllAsRead();
                      }}
                      style={{ fontSize: '0.75rem', textDecoration: 'none' }}
                    >
                      Mark all as read
                    </button>
                  )}
                </span>
                <div className="dropdown-divider" />
                {notifications.length === 0 ? (
                  <div href="#" className="dropdown-item dropdown-footer" onClick={(e) => e.preventDefault()}>
                    No notifications
                  </div>
                ) : (
                  <>
                    {/* Show all notifications - both read and unread */}
                    {notifications.map((notification) => (
                      <div
                        key={notification.id}
                        href="#"
                        className={`dropdown-item ${!notification.read ? 'bg-light' : ''}`}
                        style={{ 
                          opacity: notification.read ? 0.8 : 1,
                          borderLeft: notification.read ? 'none' : '3px solid #007bff'
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          handleNotificationClick(notification);
                        }}
                      >
                        <div className="media">
                          <div className="media-body">
                            <h3 className="dropdown-item-title" style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                              {notification.title}
                              {!notification.read && (
                                <span className="float-right text-primary">
                                  <i className="fas fa-circle" style={{ fontSize: '0.5rem' }} />
                                </span>
                              )}
                              {notification.read && (
                                <span className="float-right text-muted" style={{ fontSize: '0.7rem' }}>
                                  <i className="far fa-check-circle" style={{ fontSize: '0.6rem' }} />
                                </span>
                              )}
                            </h3>
                            <p className="text-sm text-muted" style={{ marginBottom: '0.25rem' }}>
                              {notification.message}
                            </p>
                            {notification.leaveType && (
                              <p className="text-sm text-info" style={{ marginBottom: '0.25rem', fontSize: '0.75rem' }}>
                                <i className="far fa-calendar mr-1" />
                                {notification.leaveType.charAt(0).toUpperCase() + notification.leaveType.slice(1)} Leave • {notification.numberOfDays} day{notification.numberOfDays !== 1 ? 's' : ''}
                              </p>
                            )}
                            <p className="text-sm text-muted" style={{ marginBottom: 0, fontSize: '0.75rem' }}>
                              <i className="far fa-clock mr-1" />
                              {formatTimestamp(notification.timestamp)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {notifications.length > 10 && (
                      <div className="dropdown-divider" />
                    )}
                  </>
                )}
              </div>
            </li>
          )}
          
          {/* User Profile Dropdown */}
          <li className="nav-item dropdown dropdown-item-logout1">
            <div className="nav-link p-1" data-toggle="dropdown" href="#">
              <div className="user-panel d-flex">
                <div className="image user_div">
                  <span className="usercharacter">{loading ? '...' : initial}</span>
                </div>
                <div className="info">
                  <span className="d-block f_black">
                    {loading ? 'Loading...' : displayName} <i className="fa-solid fa-angle-down" />
                  </span>
                </div>
              </div>
            </div>
            <div className="dropdown-menu dropdown-menu-right">
              <Link to="/profile" className="dropdown-item mb-2">
                <i className="fa-solid fa-user mr-2" /> Profile
              </Link>
              <div className="dropdown-item dropdown-item-logout" onClick={handleLogout}>
                <i className="fa-solid fa-arrow-right-from-bracket mr-2" /> Logout
              </div>
            </div>
          </li>
        </ul>
      </nav>
    </div>
  );
};

export default Header;